var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState } from 'react';
import { DrawerHeader, DrawerBody, DrawerFooter, Button, Alert, Skeleton, SkeletonLine, LoadingButton, } from '@one-thd/sui-atomic-components';
import Markdown from 'react-markdown';
import { Flag } from '@one-thd/sui-icons';
import { LazyImage } from '../../../shared/LazyImage';
import { ChallengesContext, DrawerContext, InfoRewardsContext, } from '../../../../contexts';
import { getImageURL } from '../../../../utils/GeneralUtil';
import { cards } from '../../../../constants';
import { ChallengeStepper } from './ChallengeStepper';
import { ChallengeError } from './ChallengeError';
import { ExpireAlert } from './ExpireAlert';
import { useIsMobile } from '../../../../hooks';
import { getSvocId } from '../../../../utils/user';
import { analyticsOverlay } from '../../../../utils/analytics';
import { removeFromUrlParams } from '../../../Utils';
import { rhAnalytics } from '../../../../constants/RewardsHub';
import { newrelic, REWARDS_HUB_CHALLENGE_VIEW, REWARDS_HUB_CHALLENGE_JOIN, REWARDS_HUB_DRAWER_CLOSE, REWARDS_HUB_CHALLENGE_NAVIGATION_BACK, } from '../../../../utils/newrelic';
var fileConstants = cards.OfferChallenges;
export var ChallengeDetailsDrawer = function () {
    var _a, _b;
    var _c = React.useContext(ChallengesContext), acceptChallenge = _c.acceptChallenge, acceptChallengeError = _c.acceptChallengeError, resetAcceptChallengeError = _c.resetAcceptChallengeError, info2Error = _c.info2Error, isChallengeLoading = _c.isChallengeLoading, isAcceptChallengeLoading = _c.isAcceptChallengeLoading, challengesInfo = _c.challengesInfo;
    var _d = React.useContext(DrawerContext), drawerOpen = _d.drawerOpen, setDrawerOpen = _d.setDrawerOpen, setDrawerType = _d.setDrawerType;
    // Track previous open state to detect close transition
    var prevOpenRef = React.useRef(drawerOpen);
    React.useEffect(function () {
        if (prevOpenRef.current && !drawerOpen) {
            // transitioned open -> closed
            resetAcceptChallengeError();
        }
        prevOpenRef.current = drawerOpen;
    }, [drawerOpen, resetAcceptChallengeError]);
    // Get the initially selected challenge from DrawerContext.
    var _e = React.useContext(DrawerContext), back = _e.back, initialChallenge = _e.details, hasChallengeError = _e.hasChallengeError;
    var isBlendedRewardsEnabled = React.useContext(InfoRewardsContext).isBlendedRewardsEnabled;
    // Get the most current challenge data
    var challenge = React.useMemo(function () {
        var _a;
        if (!(initialChallenge === null || initialChallenge === void 0 ? void 0 : initialChallenge.challengeId))
            return initialChallenge;
        // First try to find updated version in challengesInfo
        var updatedFromInfo = (_a = challengesInfo === null || challengesInfo === void 0 ? void 0 : challengesInfo.challenges) === null || _a === void 0 ? void 0 : _a.find(function (challengeState) {
            return challengeState.challengeId === initialChallenge.challengeId;
        });
        // Use the most recent version available
        return updatedFromInfo || initialChallenge;
    }, [initialChallenge, challengesInfo]);
    var fallBackChallengeImage = getImageURL('ChallengeIcon.svg');
    // State to track if the primary image URL has failed to load.
    var _f = useState(false), imageLoadFailed = _f[0], setImageLoadFailed = _f[1];
    // Track challenge view when component mounts with challenge data
    React.useEffect(function () {
        if (challenge === null || challenge === void 0 ? void 0 : challenge.challengeId) {
            // Track challenge view
            newrelic.addPageAction(REWARDS_HUB_CHALLENGE_VIEW, {
                result: 'success',
                challengeId: challenge.challengeId,
                challengeName: challenge.name,
                optedStatus: challenge.optedStatus,
            });
        }
    }, [challenge]);
    React.useEffect(function () {
        // Handle analytics
        if ((challenge === null || challenge === void 0 ? void 0 : challenge.optedStatus) === 'OPTED_IN') {
            analyticsOverlay(rhAnalytics.challengeOptedIn);
        }
        else if ((challenge === null || challenge === void 0 ? void 0 : challenge.optedStatus) === 'NOT_OPTED') {
            analyticsOverlay(rhAnalytics.challengeEnrolled);
        }
        // Handle image loading
        setImageLoadFailed(false);
        if (challenge === null || challenge === void 0 ? void 0 : challenge.imageUrl) {
            var img = new Image();
            img.src = challenge.imageUrl;
            img.onerror = function () {
                setImageLoadFailed(true);
            };
        }
    }, [challenge]);
    var handleClose = React.useCallback(function () {
        // Track drawer close
        newrelic.addPageAction(REWARDS_HUB_DRAWER_CLOSE, {
            result: 'success',
            drawerType: 'ChallengeDetails',
            source: 'header-close',
        });
        setDrawerOpen(false);
    }, [setDrawerOpen]);
    var handleBack = function () {
        // Track navigation back
        newrelic.addPageAction(REWARDS_HUB_CHALLENGE_NAVIGATION_BACK, {
            result: 'success',
            fromDrawer: 'ChallengeDetails',
            toDrawer: 'OffersList',
        });
        resetAcceptChallengeError();
        removeFromUrlParams('drawerList');
        if (isBlendedRewardsEnabled) {
            setDrawerType('RewardsList');
        }
        else {
            setDrawerType('OffersList');
        }
    };
    var handleJoinChallenge = function () {
        // Track join attempt
        newrelic.addPageAction(REWARDS_HUB_CHALLENGE_JOIN, {
            result: 'attempt',
            challengeId: challenge.challengeId,
            challengeName: challenge.name,
        });
        // Build proper challenge accept request with v2 fields
        var challengeRequest = __assign({ challengeId: challenge.challengeId }, (challenge.v2Challenge && __assign({ isV2: true }, (challenge.customerChallengeId && {
            customerChallengeId: challenge.customerChallengeId,
        }))));
        acceptChallenge(challengeRequest);
    };
    var NotOptedContent = function () { return (React.createElement("div", { className: "sui-pb-8 sui-text-base sui-leading-normal" },
        React.createElement(Markdown, { className: "drawer-details-markdown", 
            // eslint-disable-next-line react/no-children-prop
            children: challenge === null || challenge === void 0 ? void 0 : challenge.description, renderers: {
                link: function (props) { return (React.createElement("a", { href: props.href, target: "_blank", rel: "noreferrer" }, props.children)); },
            } }),
        React.createElement("p", null, cards.OfferChallenges.beginChallenge),
        React.createElement("div", { className: "sui-flex sui-grow-0 sui-shrink-0 sui-basis-auto sui-justify-end sui-w-full sui-pb-1 sui-pt-7" },
            React.createElement(LoadingButton, { variant: "primary", loading: isAcceptChallengeLoading, onClick: handleJoinChallenge, fullWidth: true },
                "Join Challenge",
                React.createElement("span", { className: "sui-ml-2" },
                    React.createElement(Flag, { size: "small" })))))); };
    var OptedInContent = function () {
        return (React.createElement("div", null,
            React.createElement("div", { className: "sui-text-base sui-leading-normal" },
                React.createElement(Markdown, { className: "drawer-details-markdown", 
                    // eslint-disable-next-line react/no-children-prop
                    children: challenge === null || challenge === void 0 ? void 0 : challenge.instructionsOverview, renderers: {
                        link: function (props) { return (React.createElement("a", { href: props.href, target: "_blank", rel: "noreferrer" }, props.children)); },
                    } }))));
    };
    var isMobile = useIsMobile().isMobile;
    var surveyLink = (_b = (_a = challenge === null || challenge === void 0 ? void 0 : challenge.challengeSteps) === null || _a === void 0 ? void 0 : _a[challenge === null || challenge === void 0 ? void 0 : challenge.challengeStepsCompleted]) === null || _b === void 0 ? void 0 : _b.url;
    var svocId = getSvocId();
    var fullSurveyLink = surveyLink &&
        "".concat(surveyLink, "?ch_id=").concat(challenge === null || challenge === void 0 ? void 0 : challenge.challengeId, "&svoc_id=").concat(svocId);
    var imageSrc = imageLoadFailed || !challenge || !challenge.imageUrl
        ? fallBackChallengeImage
        : challenge.imageUrl;
    // If there's a challenge error from page load, show the error card
    if (hasChallengeError || info2Error) {
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "sui-pt-3" }, back ? (React.createElement(DrawerHeader, { title: "Offer Details", onClose: handleClose, onBack: handleBack, className: "sui-flex sui-flex-col sui-pl-4 sui-pr-2" })) : (React.createElement(DrawerHeader, { title: "Offer Details", onClose: handleClose, className: "sui-flex sui-flex-col sui-pl-4 sui-pr-2" }))),
            React.createElement("div", { className: "hide-shadow-div" }),
            React.createElement(DrawerBody, null,
                React.createElement(ChallengeError, null))));
    }
    if (isChallengeLoading) {
        return React.createElement(ChallengeSkeleton, null);
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "sui-pt-3" }, back ? (React.createElement(DrawerHeader, { title: "Offer Details", onClose: handleClose, onBack: handleBack, className: "sui-flex sui-flex-col sui-pl-4 sui-pr-2" })) : (React.createElement(DrawerHeader, { title: "Offer Details", onClose: handleClose, className: "sui-flex sui-flex-col sui-pl-4 sui-pr-2" }))),
        React.createElement("div", { className: "hide-shadow-div" }),
        React.createElement(DrawerBody, null, isChallengeLoading || !challenge ? (React.createElement(ChallengeSkeleton, null)) : (
        // Now that we know 'challenge' exists, we can safely render the details.
        React.createElement("div", { className: isMobile ? '' : 'sui-px-2 sui-pt-1' },
            React.createElement("div", { className: "sui-flex sui-flex-col sui-justify-center sui-align-start" },
                React.createElement("div", { className: "sui-flex sui-flex-row sui-justify-start sui-pb-6" },
                    React.createElement("div", { style: { minWidth: '56px' }, className: "sui-flex sui-flex-col sui-justify-center sui-mr-6" },
                        React.createElement(LazyImage, { src: imageSrc, altText: "Challenge-Image", width: "56px", height: "56px" })),
                    React.createElement("div", { className: "sui-flex sui-flex-col sui-justify-center" },
                        React.createElement("p", { className: "sui-font-bold sui-pt-2 sui-text-2xl sui-leading-tight" }, (challenge === null || challenge === void 0 ? void 0 : challenge.name) || ''))),
                challenge.optedStatus === 'NOT_OPTED' ? (React.createElement(NotOptedContent, null)) : (React.createElement(OptedInContent, null)),
                !info2Error && challenge.optedStatus === 'OPTED_IN' && (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: "sui-pb-8 sui-pt-4" },
                        React.createElement("div", { className: "sui-font-bold sui-leading-tight sui-text-lg sui-pb-2" }, fileConstants.toComplete),
                        React.createElement(ChallengeStepper, { steps: challenge.challengeSteps, activeStep: challenge.challengeStepsCompleted, url: fullSurveyLink })),
                    React.createElement(ExpireAlert, { challenge: challenge }))),
                !info2Error && challenge.optedStatus !== 'OPTED_IN' && (React.createElement(ExpireAlert, { challenge: challenge })),
                challenge.termsAndConditions && (React.createElement("div", { className: "sui-text-base" },
                    React.createElement("div", { className: "sui-pt-4 sui-font-bold" }, "Challenge Terms & Conditions"),
                    React.createElement("div", { className: "sui-pt-2 sui-leading-relaxed" },
                        React.createElement(Markdown, { className: "drawer-details-markdown", renderers: {
                                link: function (props) { return (React.createElement("a", { href: props.href, target: "_blank", rel: "noreferrer" }, props.children)); },
                            } }, challenge.termsAndConditions))))),
            acceptChallengeError && (React.createElement("div", { className: "sui-pt-4" },
                React.createElement(Alert, { status: "error" }, cards.OfferChallenges.acceptedError)))))),
        surveyLink && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "sui-p-2" },
                React.createElement(DrawerFooter, null,
                    React.createElement(Button, { fullWidth: true, href: fullSurveyLink, target: "_blank" }, "Complete Survey")))))));
};
export var ChallengeSkeleton = function () {
    return (React.createElement(Skeleton, { grow: true },
        React.createElement(SkeletonLine, { variant: "multi", numberOfLines: 9 })));
};
